import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import { useUserVisibilityPreferences } from '@/hooks/useUserVisibilityPreferences';
import {
  Avatar,
  AvatarDropdownMenuButton,
  AvatarGroup,
  Tooltip,
  UserAvatar,
} from '@payaca/ambrosia';

import { useMemo } from 'react';

export const UNASSIGNED_USER_ID_SENTINEL = 'unassigned';

type Props = {
  selectedUserIds: string[];
  onChange: (next: string[]) => void;
  includeUnassigned?: boolean;
  disabled?: boolean;
  minVisible?: number;
  preferredUserIds?: string[];
};

function AccountUserAvatarSelector(props: Props) {
  const {
    selectedUserIds,
    onChange,
    includeUnassigned = false,
    disabled,
    minVisible = 4,
    preferredUserIds,
  } = props;

  const { data: accountUsers = [] } = useGetMyAccountUsersExcludedDeactivated();

  const users = useMemo(
    () =>
      accountUsers.map((user) => ({
        ...user,
        id: user.publicId,
        name: user.fullName,
      })),
    [accountUsers]
  );

  const selectedRealUserIds = useMemo(
    () =>
      selectedUserIds.filter(
        (id) => Boolean(id) && id !== UNASSIGNED_USER_ID_SENTINEL
      ),
    [selectedUserIds]
  );
  const isUnassignedSelected = selectedUserIds.includes(
    UNASSIGNED_USER_ID_SENTINEL
  );

  const { visibleUsers, recordClick } = useUserVisibilityPreferences({
    users,
    selectedIds: selectedRealUserIds,
    minVisible,
    preferredUserIds,
  });

  const toggleId = (id: string) => {
    const isSelected = selectedUserIds.includes(id);
    const next = isSelected
      ? selectedUserIds.filter((existing) => existing !== id)
      : [...selectedUserIds, id];
    onChange(next);
  };

  const handleUserSelect = (publicId: string) => {
    recordClick(publicId);
    toggleId(publicId);
  };

  const handleUnassignedSelect = () => {
    toggleId(UNASSIGNED_USER_ID_SENTINEL);
  };

  const mapUserToAvatar = (user: (typeof users)[number]) => ({
    firstName: user.firstName,
    lastName: user.lastName,
    emailAddress: user.email,
    userColour: user.colour,
    imgSrc: user.avatarUrl,
  });

  const totalSelectedCount =
    selectedRealUserIds.length + (isUnassignedSelected ? 1 : 0);

  const menuItems = [
    {
      heading: `Users (${totalSelectedCount} selected)`,
      items: [
        ...(includeUnassigned
          ? [
              {
                label: (
                  <div className="flex items-center gap-2">
                    <Avatar
                      iconName="user-x-01"
                      sizeVariant="xs"
                      colour="#CCCCCC"
                    />
                    <span className="flex-1">Unassigned</span>
                  </div>
                ),
                onClick: handleUnassignedSelect,
                searchKey: 'Unassigned',
                selected: isUnassignedSelected,
              },
            ]
          : []),
        ...users.map((user) => {
          const isSelected = selectedRealUserIds.includes(user.publicId);

          return {
            label: (
              <div className="flex items-center gap-2">
                <UserAvatar user={mapUserToAvatar(user)} sizeVariant="xs" />
                <span className="flex-1">{user.fullName}</span>
              </div>
            ),
            onClick: () => handleUserSelect(user.publicId),
            searchKey: user.fullName ?? '',
            selected: isSelected,
          };
        }),
      ],
    },
  ];

  return (
    <div className={disabled ? 'pointer-events-none opacity-50' : undefined}>
      <AvatarGroup sizeVariant="xs">
        {includeUnassigned && (
          <button
            type="button"
            onClick={handleUnassignedSelect}
            disabled={disabled}
            className="bg-transparent border-0 p-0 cursor-pointer outline-none"
          >
            <Tooltip tooltipContent="Unassigned">
              <Avatar
                iconName="user-x-01"
                sizeVariant="xs"
                colour="#CCCCCC"
                borderColourVariant={isUnassignedSelected ? 'blue' : 'white'}
              />
            </Tooltip>
          </button>
        )}
        {visibleUsers.map((user) => {
          const isSelected = selectedRealUserIds.includes(user.publicId);
          return (
            <button
              key={user.publicId}
              type="button"
              onClick={() => handleUserSelect(user.publicId)}
              disabled={disabled}
              className="bg-transparent border-0 p-0 cursor-pointer outline-none"
            >
              <UserAvatar
                user={mapUserToAvatar(user)}
                enableUserInfoTooltip={!disabled}
                sizeVariant="xs"
                borderColourVariant={isSelected ? 'blue' : 'white'}
              />
            </button>
          );
        })}

        {users.length + (includeUnassigned ? 1 : 0) > 1 && !disabled && (
          <AvatarDropdownMenuButton
            menuItems={menuItems}
            className="w-60 h-64 outline-none"
            hasSearch
          />
        )}
      </AvatarGroup>
    </div>
  );
}

export default AccountUserAvatarSelector;
